(ns ui.view.entity-creator.view
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.modal.view :as modal]
   [ui.enrich.drop-section :as enrich-drop]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.entity-creator.components :as components]
   [ui.view.entity-creator.event]
   [ui.view.entity-creator.model :as m]
   [ui.view.entity-creator.sub]
   [ui.view.entity-creator.utils :as u]))

(defn get-edited-attribute-value
  [edited-values attribute]
  (if (keyword? attribute)
    (get edited-values attribute)
    (get-in edited-values attribute)))

(defn resolve-creator-fields
  [{:keys [entity-type pipeline stage-id fields-config
           field-schemas field-schema-overrides]}]
  (->> fields-config
       (keep (fn [{:keys [attribute stages create] :as _field}]
               (let [field-config (if stage-id
                                    (get stages stage-id)
                                    create)
                     fs (get field-schemas attribute)
                     overrides (get field-schema-overrides attribute)
                     fs (if overrides
                          (potpuri/deep-merge fs overrides)
                          fs)
                     read-only? (or (:read-only? fs)
                                    (u/read-only-field? {:attribute attribute
                                                         :entity-type entity-type
                                                         :pipeline pipeline}))
                     secondary-fs (some->> fs
                                           :field/secondary-attribute
                                           (get field-schemas))
                     fs (cond-> fs
                          secondary-fs (assoc :secondary-field-schema secondary-fs))]
                 (when-not (u/hidden-field? {:field-config field-config
                                             :field-schema fs
                                             :attribute attribute
                                             :entity-type entity-type
                                             :pipeline pipeline})
                   {:attribute attribute
                    :field-config field-config
                    :field-schema fs
                    :read-only? read-only?}))))
       vec))

(defn first-blank-focus-attribute
  [fields edited-values]
  (some->> fields
           (cu/find-first
            (fn [{:keys [attribute read-only?]}]
              (and (not read-only?)
                   (cu/blank-value?
                    (get-edited-attribute-value edited-values attribute)))))
           :attribute))

#_(defn module-access?
    [entity-type]
    (case entity-type
      :entity.type/company true
      :entity.type/real-estate true
      :entity.type/fof-asset true
      :entity.type/other-asset true
      :entity.type/person true
      :entity.type/investor true
      :entity.type/oppo (session/dealflow?)
      :entity.type/fundraising-opportunity (session/fundraising?)
      :entity.type/holding (session/portfolio?)
      :entity.type/any true
      false))

(defn editor-component
  [{:keys [entity-type attribute ident auto-focus?] :as args}
   {:keys [field-schema field-config read-only?
           validate-at-start?]}]
  (r/with-let [local-state (r/atom {})
               apply-validations! #(swap! local-state assoc :modified? true)]
    (let [label (loc/field-name attribute field-schema)
          placeholder (field/placeholder field-schema)
          mandatory? (= (:setting field-config)
                        :mandatory)
          label (if mandatory?
                  (str label " *")
                  label)
          value @(rf/subscribe [m/edited-value args])
          pipeline @(rf/subscribe [m/pipeline args])
          display-errors? (or validate-at-start?
                              (:modified? @local-state))
          errors (when display-errors?
                   @(rf/subscribe [m/field-errors args]))
          pipeline-id (:db/id pipeline)
          {:field/keys [data-type secondary-attribute secondary-field-schema
                        entity-types not-clearable? os-filter-terms-fn]} field-schema
          select-entity-types (field/select-entity-types field-schema)
          create-entity-types (field/create-entity-types field-schema)

          ;;TODO pipeline/target-asset-types when applicable:
          entity-types (if (= entity-types [:entity.type/asset])
                         @session/available-asset-types
                         entity-types)
          select-entity-types (if (= select-entity-types [:entity.type/asset])
                                @session/available-asset-types
                                select-entity-types)
          create-entity-types (if (= create-entity-types [:entity.type/asset])
                                @session/available-asset-types
                                create-entity-types)
          named-value-attribute (:named-value-attribute field-schema)
          secondary-value (when secondary-attribute
                            @(rf/subscribe [m/edited-value
                                            (assoc args
                                                   :attribute secondary-attribute)]))
          currency? (= data-type :data.type/currency-value)
          secondary-value (if currency?
                            (or secondary-value
                                @session/default-currency
                                :eur)
                            secondary-value)
          ref-multi? (= data-type :data.type/ref-multi)
          search-input? (contains? data-types/ref-types data-type)
          edit! (fn [{:keys [conj? attribute]} val option-val]
                  (apply-validations!)
                  (let [value (if search-input?
                                option-val
                                val)]
                    (rf/dispatch-sync
                     [m/edit-value
                      (cond-> args
                        true (assoc :attribute attribute
                                    :new-value value)
                        conj? (assoc :conj? true))])))
          ;; Don't pass field-schema - explicit args for field-editor
          primary-args (merge
                        ;; :editor.type/search data-input (i.e., open-search) requires
                        ;; some field-schema-specific args: making these explicit
                        ;; for now:
                        (when search-input?
                          (u/search-editor-args
                           {:entity-types entity-types
                            :select-entity-types select-entity-types
                            :not-clearable? not-clearable?
                            :os-filter-terms-fn os-filter-terms-fn
                            :pipeline-id pipeline-id}))
                        (when named-value-attribute
                          {:named-value-attribute named-value-attribute})
                        {:attribute attribute
                         :disabled? read-only?
                         :read-only? read-only?
                         :auto-focus? auto-focus?
                         :label label
                         :placeholder placeholder
                         :on-change (r/partial edit! {:attribute attribute})
                         ;;note: select-component calls on-blur with nil, text-input
                         ;; with the input value
                         :on-blur (fn [_val]
                                    (apply-validations!))
                         :value value
                         :error (when display-errors?
                                  (seq errors))
                         :portal-target (some-> args :portal-target-atom deref)
                         :create! (when (u/allow-create?
                                         {:field-schema field-schema
                                          :entity-type entity-type
                                          :attribute attribute
                                          :pipeline-id pipeline-id})
                                    (fn [query]
                                      (f-entity/open-creator!
                                       create-entity-types
                                       {:named-value-attribute attribute
                                        :for-attribute attribute
                                        :for-entity-type entity-type
                                        :ident (into ident [:nested attribute])
                                        :skip-success-msg? true
                                        :start-value query
                                        :on-ok!
                                        (fn [{:keys [new-entity-id
                                                     new-entity
                                                     close-cb!] :as _ok-args}]
                                          (edit! {:attribute attribute
                                                  :conj? ref-multi?}
                                                 new-entity-id
                                                 {:value new-entity-id
                                                  :label (entity/display-string
                                                          new-entity)})
                                          (close-cb!))})))
                         ;;TODO rf-sub?:
                         :options (u/resolve-options
                                   {:field-schema field-schema
                                    :pipeline-id pipeline-id})
                         :editor-type (when search-input?
                                        :editor.type/search)
                         :data-type data-type})
          secondary-args (when secondary-attribute
                           (assoc secondary-field-schema
                                  :attribute secondary-attribute
                                  :disabled? read-only?
                                  :value secondary-value
                                  :editor-type :editor.type/select
                                  :on-change (r/partial edit!
                                                        {:attribute
                                                         secondary-attribute})))]
      [:div
       {:class (uu/cm "flex flex-col gap-1")}
       [components/field-editor
        (cond-> primary-args
          secondary-args (assoc :secondary-args secondary-args))]])))

(defn creator-view
  ;; `:fields-config` in args overrides the resolved config — propagated
  ;; transparently via `@(rf/subscribe [m/config args])` (see the `(or
  ;; fields-config ...)` short-circuit in the `m/config` sub). If you use
  ;; the override, the SAME args must also be used to subscribe to
  ;; `m/entity-validations` (or any sub depending on `m/config`) so the
  ;; rendered field set matches the validated field set.
  [{:keys [entity-type field-schema-overrides auto-focus-first-blank?
           _entity-edits-path] :as args}]
  (let [et-config @(rf/subscribe [m/config args])
        stage @(rf/subscribe [m/stage args])
        pipeline @(rf/subscribe [m/pipeline args])
        stage-id (:db/id stage)
        fields (:fields et-config)
        field-schemas @(rf/subscribe [m/field-schemas args])
        custom-edits @(rf/subscribe [m/custom-edits
                                     (select-keys args
                                                  [:ident :entity-edits-path])])
        fields (resolve-creator-fields
                {:entity-type entity-type
                 :pipeline pipeline
                 :stage-id stage-id
                 :fields-config fields
                 :field-schemas field-schemas
                 :field-schema-overrides field-schema-overrides})
        auto-focus-attribute (when auto-focus-first-blank?
                               (first-blank-focus-attribute fields custom-edits))]
    [:div
     {:class (uu/cm "flex flex-col gap-2")}
     (if (seq fields)
       (doall
        (for [{:keys [attribute field-config field-schema read-only?]} fields
              :let [fs field-schema]]
          ^{:key (str "field-input-" attribute)}
          [editor-component
           (cond-> (assoc args
                          :attribute attribute)
             (= attribute auto-focus-attribute)
             (assoc :auto-focus? true))
           {:field-config field-config
            :field-schema fs
            :read-only? read-only?}]))
       [:div
        {:class (uu/cm "text-sm m-0 text-base-light-text-paler")}
        (loc/tr :creator/no-fields-configured)])]))

(defn single-creator-view
  [args]
  (let [entity-type @(rf/subscribe [m/entity-type args])]
    [:div
     [creator-view (assoc args
                          :entity-type entity-type
                          :entity-edits-path m/entity-edits-path)]]))

(defn processing-layer
  [_props]
  [:div
   {:class (uu/cm "absolute inset-0 bg-black bg-opacity-40 z-50 flex items-center"
                  "margin -m-4"
                  "justify-center pointer-events-auto")}
   [:div.spinner-wrapper
    [:i.fal.fa-spinner.fa-pulse.fa-4x]
    [:div {:class (uu/cm "text-white text-lg")}
     (loc/tr :processing)]]])

(defn finished-view
  [args]
  (let [entity-type @(rf/subscribe [m/entity-type args])]
    [:div
     [:p
      {:class "p-6 flex flex-col gap-4 justify-center items-center text-bold"}
      [:img
       {:src   "/img/sent-success.png"
        :class "max-w-48"}]
      (loc/tr :entity-created-successfully [(loc/tr entity-type)])]]))

(defn view
  [{:keys [portal-target-atom] :as props}]
  (let [subview @(rf/subscribe [m/subview props])
        processing? @(rf/subscribe [m/processing? props])]
    [:div
     {:ref (fn [el]
             (when el
               (reset! portal-target-atom el)))
      :class (uu/cm "relative")}
     (case subview
       :creator-one
       [single-creator-view props]

       :finished
       [finished-view props]

       :loading
       nil

       (str subview " (error)"))

     (when processing?
       [processing-layer props])]))

(defn footer
  [props close-modal!]
  (let [subview @(rf/subscribe [m/subview props])
        processing? @(rf/subscribe [m/processing? props])]
    (if processing?
      [components/processing-footer props close-modal!]
      (case subview
        :creator-one
        [components/creator-footer props close-modal!]

        :finished
        [components/finished-footer props close-modal!]

        "[buttons]"))))

(defn add-ident
  [{:keys [ident] :as props}]
  (let [ident (if ident
                ident
                (-> "creator-"
                    gensym
                    str
                    keyword
                    vector))]
    (assoc props :ident ident)))

(defn modal-view
  [{:keys [shown? topmost? close-modal! props] :as _modal-props}]
  (let [portal-target-atom (atom nil)
        ;; if props don't contain :ident => generate one:
        props (add-ident props)
        close! (fn [_]
                 (rf/dispatch [m/close-creator props])
                 (when (fn? (:on-close! props))
                   ((:on-close! props)))
                 (close-modal!))
        close-modal! (fn []
                       (when (fn? (:on-close! props))
                         ((:on-close! props)))
                       (close-modal!))]
    ;;TODO remove 'when-not' - used only for development time convenience
    (when-not @(rf/subscribe [m/entity-type props])
      ;;TODO proper place?
      (rf/dispatch-sync [m/open-creator props {:on-ok! (:on-ok! props)
                                               :on-processed! (:on-processed! props)
                                               :close-modal! close-modal!}]))
    (fn [{:keys []}]
      (let [finished? @(rf/subscribe [m/created-entity props])
            props (assoc props :portal-target-atom portal-target-atom)]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         (if finished?
                           (fn []
                             (rf/dispatch [m/finish props]))
                           close!)
          :content-class (uu/cm "")
          #_#_:close-button? (not finished?)}
         @(rf/subscribe [m/title props])
         [view props]
         [footer props close-modal!]]))))

(defn curtain-view
  [{:keys [shown? topmost? close-modal! props] :as _modal-props}]
  (let [portal-target-atom (atom nil)
        ;; if props don't contain :ident => generate one:
        props (add-ident props)
        close! (fn [_]
                 (rf/dispatch [m/close-creator props])
                 (when (fn? (:on-close! props))
                   ((:on-close! props)))
                 (close-modal!))
        close-modal! (fn []
                       (when (fn? (:on-close! props))
                         ((:on-close! props)))
                       (close-modal!))]
    (when-not @(rf/subscribe [m/entity-type props])
      (rf/dispatch-sync [m/open-creator props {:on-ok! (:on-ok! props)
                                               :on-processed! (:on-processed! props)
                                               :close-modal! close-modal!}]))
    (fn [{:keys []}]
      (let [finished?    @(rf/subscribe [m/created-entity props])
            props        (assoc props :portal-target-atom portal-target-atom)
            ;; AI enrichment: drop a PDF to pre-fill the configured fields. The
            ;; backend descriptor is driven by the curtain's own configured field
            ;; set (so only those are filled); completion + resume are global.
            entity-type  (:entity-type props)
            config-path  (:main-config-path (ecu/resolve-config-paths props))
            field-kws    (->> @(rf/subscribe [m/config (assoc props :config-path config-path)])
                              :fields (keep :attribute) vec)
            ;; What the user has entered so far: the review restores it under
            ;; the AI's suggestions.
            draft        (u/enrich-draft
                          (registry/get-schema @(rf/subscribe [::session-sub/tenant-schema])
                                               entity-type)
                          @(rf/subscribe [m/entity-edits props]))
            show-enrich? (and (not finished?)
                              (seq field-kws)
                              (session/has-feature-access? :feature/ai-enrichment))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         (if finished?
                           (fn []
                             (rf/dispatch [m/finish props]))
                           close!)
          :outer-content-class (uu/cm "justify-start")
          :header-child-class "truncate"
          :content-class (uu/cm
                          ;; for zapkit-components styling:
                          "tw2"
                          (if false
                            "w-9/12"
                            nil)
                          "mx-0 ml-auto h-full max-h-full mb-1"
                          "min-w-[400px] w-1/4")
          :close-button? true}
         @(rf/subscribe [m/title props])
         [:<>
          [view props]
          (when show-enrich?
            [enrich-drop/section
             {:request-fn (fn [attachment-id]
                            {:entity-type    entity-type
                             :targets        [{:entity-type entity-type :fields field-kws}]
                             :draft          draft
                             :attachment-ids [attachment-id]})}])]
         [footer (assoc props :curtain? true) close-modal!]]))))
